import React, { useState } from "react";
import { X, Send, Loader2, Check, Trash2 } from "lucide-react";
import { toast } from "sonner";
import * as api from "../api";
import ResendPanel from "./ResendPanel";

// Impostazioni → Server di invio alternativo (relay SMTP): per account, usato quando il provider rifiuta l'IP del server
// Suffisso mostrato accanto alla casella nell'elenco: Resend, relay manuale o niente
function descrizioneRelay(a) {
  if (a.relay_modo === "resend") return " · Resend";
  if (a.relay_host) return ` · relay: ${a.relay_host}`;
  return "";
}

export default function RelayModal({ open, onClose, accounts, onSaved }) {
  const [accId, setAccId] = useState("");
  const [host, setHost] = useState("");
  const [port, setPort] = useState("587");
  const [user, setUser] = useState("");
  const [password, setPassword] = useState("");
  const [modo, setModo] = useState("riserva");
  const [resendOk, setResendOk] = useState(false);
  const [busy, setBusy] = useState(false);
  if (!open) return null;

  const lista = (accounts || []).filter((a) => a.id !== "tutti" && a.email);
  const scelto = lista.find((a) => a.id === accId) || null;
  const seleziona = (id) => {
    setAccId(id);
    const a = lista.find((x) => x.id === id);
    setHost(a?.relay_host || "");
    setPort(String(a?.relay_port || 587));
    setUser(a?.relay_user || "");
    setModo(a?.relay_modo || "riserva");
    setPassword("");
  };

  const salva = async (rimuovi = false) => {
    if (!scelto) return toast.error("Scegli la casella");
    setBusy(true);
    try {
      const payload = (() => {
        if (rimuovi) return { host: "" };
        if (modo === "resend") return { host: "", modo: "resend" };
        return { host, port: parseInt(port, 10) || 587, user, password, modo };
      })();
      const esito = (() => {
        if (rimuovi) return "Relay rimosso";
        if (modo === "resend") return `Invio via Resend attivo per ${scelto.email}`;
        return `Relay ${host} salvato per ${scelto.email}`;
      })();
      const r = await api.setRelay(scelto.id, payload);
      toast.success(esito);
      onSaved?.(scelto.id, r.relay);
      if (rimuovi) { setHost(""); setUser(""); setPassword(""); }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Salvataggio non riuscito");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="wf-modal-backdrop show" onClick={onClose} data-testid="relay-backdrop">
      <div className="wf-modal relay-modal" onClick={(e) => e.stopPropagation()} data-testid="relay-modal">
        <div className="wf-modal-head">
          <span className="wf-modal-title"><Send size={19} strokeWidth={2.3} /> Server di invio alternativo</span>
          <button className="icon-btn" onClick={onClose} aria-label="Chiudi" data-testid="relay-close"><X size={20} strokeWidth={2.6} /></button>
        </div>
        <p className="manu-text">
          Se il tuo provider rifiuta l'invio dal server dell'app (es. Aruba: "554 Connessione rifiutata dall'IP"), le email
          partono da questo server SMTP di riserva mantenendo il tuo indirizzo come mittente.
        </p>
        <label className="manu-label">Casella</label>
        <select className="wf-input" value={accId} onChange={(e) => seleziona(e.target.value)} data-testid="relay-account">
          <option value="">— scegli —</option>
          {lista.map((a) => (
            <option key={a.id} value={a.id}>{a.email}{descrizioneRelay(a)}</option>
          ))}
        </select>
        {scelto && (
          <>
            <div className="host-port" style={{ marginTop: 10 }}>
              <div className="wf-field">
                <label className="manu-label">Server SMTP relay</label>
                <input className="wf-input" placeholder="smtp.relay.it" value={host} onChange={(e) => setHost(e.target.value)} data-testid="relay-host" />
              </div>
              <div className="wf-field">
                <label className="manu-label">Porta</label>
                <input className="wf-input" type="number" min="1" max="65535" value={port} onChange={(e) => setPort(e.target.value)} data-testid="relay-port" />
              </div>
            </div>
            <label className="manu-label">Utente (vuoto = {scelto.email})</label>
            <input className="wf-input" value={user} onChange={(e) => setUser(e.target.value)} placeholder={scelto.email} data-testid="relay-user" />
            <label className="manu-label">Password relay {scelto.relay_host ? "(lascia vuoto per non cambiarla)" : ""}</label>
            <input className="wf-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="relay-password" />
            <label className="manu-label">Quando usarlo</label>
            <div className="proto-row">
              {[["riserva", "Solo se il provider rifiuta"], ["sempre", "Sempre"], ["resend", "Resend (API)"]].map(([v, l]) => (
                <button key={v} type="button" className={`wf-btn proto-btn ${modo === v ? "primary" : ""}`} onClick={() => setModo(v)} data-testid={`relay-modo-${v}`}>{l}</button>
              ))}
            </div>
            {modo === "resend" && (
              <>
                <p className="manu-text" style={{ marginTop: 8 }}>
                  Le email di {scelto.email} partono via Resend (HTTPS, nessun SMTP): Aruba non può bloccarle. Serve il dominio verificato.
                </p>
                <ResendPanel dominio={scelto.email.split("@")[1]} onVerificato={setResendOk} />
              </>
            )}
            <div className="proto-row" style={{ marginTop: 14 }}>
              <button className="wf-btn primary proto-btn" onClick={() => salva(false)} disabled={busy || (modo === "resend" ? !resendOk : !host)} data-testid="relay-save">
                {busy ? <Loader2 size={16} className="spin" /> : <Check size={16} />} {modo === "resend" ? "Attiva Resend" : "Salva e verifica"}
              </button>
              {(scelto.relay_host || scelto.relay_modo === "resend") && (
                <button className="wf-btn proto-btn" onClick={() => salva(true)} disabled={busy} data-testid="relay-remove"><Trash2 size={16} /> Rimuovi</button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
